import { useState } from 'react';
import type { RegistryProfile } from '@/hooks/use-registries';

interface AddRegistryFormProps {
    onSubmit: (data: Partial<RegistryProfile>) => Promise<void>;
    isSubmitting: boolean;
}

export function AddRegistryForm({ onSubmit, isSubmitting }: AddRegistryFormProps) {
    const [alias, setAlias] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [kind, setKind] = useState<'local' | 'cloud' | 'partner'>('cloud');
    const [natsUrl, setNatsUrl] = useState('');
    const [baseUrl, setBaseUrl] = useState('');
    const [trustTier, setTrustTier] = useState<'trusted' | 'verified' | 'untrusted'>(
        'untrusted',
    );
    const [supportsFederation, setSupportsFederation] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await onSubmit({
            alias,
            display_name: displayName || undefined,
            kind,
            nats_url: natsUrl || undefined,
            base_url: baseUrl || undefined,
            trust_tier: trustTier,
            supports_federation: supportsFederation,
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                    Alias <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    value={alias}
                    onChange={(e) => setAlias(e.target.value)}
                    placeholder="e.g., cloud, partner-acme"
                    required
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-slate-500">
                    Unique identifier for this registry connection.
                </p>
            </div>

            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                    Display Name
                </label>
                <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="e.g., More0 Cloud"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                    Kind
                </label>
                <select
                    value={kind}
                    onChange={(e) => setKind(e.target.value as typeof kind)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                    <option value="local">Local</option>
                    <option value="cloud">Cloud</option>
                    <option value="partner">Partner</option>
                </select>
            </div>

            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                    NATS URL
                </label>
                <input
                    type="text"
                    value={natsUrl}
                    onChange={(e) => setNatsUrl(e.target.value)}
                    placeholder="nats://host:4222"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                    Base URL
                </label>
                <input
                    type="text"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder="https://registry.example.com"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                    Trust Tier
                </label>
                <select
                    value={trustTier}
                    onChange={(e) => setTrustTier(e.target.value as typeof trustTier)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                    <option value="trusted">Trusted</option>
                    <option value="verified">Verified</option>
                    <option value="untrusted">Untrusted</option>
                </select>
                <p className="mt-1 text-xs text-slate-500">
                    Controls how cards from this registry are indexed and displayed.
                </p>
            </div>

            <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    id="supports_federation"
                    checked={supportsFederation}
                    onChange={(e) => setSupportsFederation(e.target.checked)}
                    className="rounded border-slate-300"
                />
                <label htmlFor="supports_federation" className="text-sm text-slate-700">
                    Supports federation (card exchange)
                </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
                <button
                    type="submit"
                    disabled={!alias || isSubmitting}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSubmitting ? 'Adding...' : 'Add Registry'}
                </button>
            </div>
        </form>
    );
}
